/**
 * V70G7B — 팝업 공지 모달. 본문은 RichContentViewer(DOMPurify) 로만 렌더링한다.
 */
import React, { useEffect, useRef } from 'react';
import { X, Paperclip } from 'lucide-react';
import RichContentViewer from './RichContentViewer';
import type { ActivePopupAnnouncement } from '../hooks/useAnnouncements';

/** V70-N — 팝업 최대 폭 (게임대회 대시보드 공지 상세 모달 maxWidth 760 과 동일) */
export const POPUP_MAX_WIDTH = 760;

interface Props {
  popup: ActivePopupAnnouncement;
  /** V70G8B: 상단 분류 라벨 (기본 '공지') */
  sourceLabel?: string;
  /** V70G8F: 진행 개수 (팝업이 2개 이상일 때만). null 이면 안내 미표시 */
  progress?: { position: number; total: number; remaining: number } | null;
  onClose: () => void;
  onHideForDay: () => void;
  onViewDetail: () => void;
  /** V70G8H: 본문 ERP 내부 링크 클릭 (외부 링크는 뷰어가 새 탭으로 연다) */
  onInternalLink?: (path: string) => void;
}

export function PopupAnnouncementModal({ popup, sourceLabel = '공지', progress = null, onClose, onHideForDay, onViewDetail, onInternalLink }: Props) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popup.id, onClose]);

  const attachmentCount = popup.attachments?.length || 0;
  const titleId = `popup-announcement-title-${popup.id}`;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="popup-announcement-dialog"
        style={{
          // V70-N — 최대 폭 560 → 760px (게임대회 공지 상세 모달과 같은 폭). 좁은 화면은 바깥 여백 16px 유지
          background: 'white', borderRadius: '14px', width: '100%', maxWidth: `${POPUP_MAX_WIDTH}px`,
          maxHeight: 'calc(100vh - 32px)', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '18px 20px 12px', borderBottom: '1px solid var(--color-gray-200)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '4px' }}>
              {sourceLabel}
            </div>
            <h2 id={titleId} style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: 'var(--color-gray-900)', wordBreak: 'break-word' }}>
              {popup.title}
            </h2>
          </div>
          {progress && (
            <span
              data-testid="popup-progress"
              aria-label={`전체 ${progress.total}개 중 ${progress.position}번째 팝업`}
              style={{
                flexShrink: 0, alignSelf: 'flex-start', marginTop: '4px', padding: '2px 8px', borderRadius: '999px',
                background: 'var(--color-gray-100)', color: 'var(--color-gray-600)',
                fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
              }}
            >
              {progress.position} / {progress.total}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="닫기"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--color-gray-500)' }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <RichContentViewer content={popup.content} format={popup.contentFormat || 'plain'} onInternalLink={onInternalLink} />
          {attachmentCount > 0 && (
            <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--color-gray-600)' }}>
              <Paperclip size={14} /> 첨부파일 {attachmentCount}개 — 상세 화면에서 확인할 수 있습니다.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid var(--color-gray-200)', background: 'var(--color-gray-50)' }}>
          {progress && progress.remaining > 0 && (
            <div
              data-testid="popup-remaining"
              role="status"
              style={{ flexBasis: '100%', fontSize: '12px', color: 'var(--color-gray-500)', lineHeight: 1.4 }}
            >
              확인할 팝업이 {progress.remaining}개 더 있습니다.
            </div>
          )}
          <div>
            {popup.popupDismissPolicy === 'hideForDay' && (
              <button
                type="button"
                onClick={onHideForDay}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', color: 'var(--color-gray-600)', textDecoration: 'underline', padding: '6px 0' }}
              >
                오늘 하루 보지 않기
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={onViewDetail}
              style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--color-gray-300)', background: 'white', cursor: 'pointer', fontSize: '13px', color: 'var(--color-gray-700)' }}
            >
              상세 보기
            </button>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              style={{ padding: '8px 18px', borderRadius: '8px', border: 'none', background: 'var(--color-primary)', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
